import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const LABEL = { whatsapp: "WhatsApp", email: "Email" };
const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const RESULT = { accepted: "Accepted by provider", sent: "Sent", delivered: "Delivered", read: "Read", failed: "Failed", unknown: "Outcome unknown — not confirmed" };
const BAD = { failed: "bg-rose-50 text-rose-700", unknown: "bg-orange-50 text-orange-800" };

// Sends via the central backend workflow and shows the provider's real result.
export function InvoiceSendDialog({ invoice, channel, onClose }) {
  const reqId = useRef(rid());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [warn, setWarn] = useState(null);
  const { data: pv, isLoading, error } = useQuery({
    queryKey: ["send-preview", invoice.id, channel],
    queryFn: async () => (await api.get(`/invoices/${invoice.id}/send-preview?channel=${channel}`)).data,
  });
  const st = pv?.channel_state;
  const live = st?.live;
  const send = async (resend = false) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = (await api.post(`/invoices/${invoice.id}/send`, { channel, request_id: reqId.current, resend })).data;
      setResult(r); setBlocked(null);
      BAD[r.status] ? toast.error(`${LABEL[channel]}: ${RESULT[r.status]} — ${r.error}`) : toast.success(`${LABEL[channel]}: ${RESULT[r.status] || r.status}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code) setBlocked(d); else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };
  const retry = async () => {
    if (!warn) {  // first click only shows the duplicate-send warning
      setWarn(result.status === "unknown" ? "The provider's response was unclear, so the customer MAY already have this message. Retrying can send a duplicate." : "The provider rejected this message. Retrying sends it once more.");
      return;
    }
    setBusy(true);
    try { const r = (await api.post(`/comm/outbox/${result.id}/retry`, { confirm_duplicate_risk: true })).data; setResult(r); setWarn(null); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const guide = blocked?.setup || (!live && st?.setup);
  return (
    <Dialog open onOpenChange={() => !busy && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="invoice-send-confirm">
        <DialogHeader><DialogTitle className="font-display">Send invoice via {LABEL[channel]}</DialogTitle></DialogHeader>
        {isLoading ? <p className="text-sm text-slate-400">Checking…</p> : error ? <p className="text-sm text-rose-600">{apiErr(error)}</p> : (
          <div className="space-y-3 text-sm">
            {pv.note && <p className="rounded-lg bg-sky-50 p-2 text-xs text-sky-800" data-testid="send-version-note">{pv.note}</p>}
            <div className="space-y-1 rounded-lg bg-slate-50 p-3">
              <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="font-mono" data-testid="send-invoice-number">{pv.invoice_number}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Customer</span><span className="font-medium">{pv.customer_name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">To</span><span className="font-mono" data-testid="send-destination">{pv.destination_masked || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total / due</span><span>₹{Number(pv.total || 0).toLocaleString("en-IN")} / ₹{Number(pv.balance || 0).toLocaleString("en-IN")}</span></div>
            </div>
            {!pv.valid && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700" data-testid="send-destination-error">
                {pv.error}. {pv.customer_id && <Link to={`/customers/${pv.customer_id}`} className="font-semibold underline" data-testid="send-fix-contact">Update customer contact</Link>}
              </div>
            )}
            {guide && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="send-not-configured">
                <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />{blocked?.msg || (st.state === "not_configured" ? `${LABEL[channel]} not configured` : `${LABEL[channel]} sending is not enabled`)} — nothing will be sent</div>
                <ul className="list-disc space-y-0.5 pl-4">{guide.map((g) => <li key={g}>{g}</li>)}</ul>
              </div>
            )}
            {["RECENTLY_SENT", "UNRESOLVED_PRIOR_SEND", "SEND_IN_PROGRESS"].includes(blocked?.code) && (
              <div className="rounded-lg border border-orange-300 bg-orange-50 p-2 text-xs text-orange-800" data-testid="send-recent">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{blocked.msg}
                {blocked.code !== "SEND_IN_PROGRESS" && <Button size="sm" onClick={() => send(true)} disabled={busy} data-testid="invoice-send-again" className="mt-2 block bg-orange-600 text-white hover:bg-orange-700">Send again anyway</Button>}
              </div>
            )}
            {result && (
              <div className={`flex items-start gap-2 rounded-lg p-2.5 text-xs ${BAD[result.status] || "bg-emerald-50 text-emerald-800"}`} data-testid="send-result">
                {BAD[result.status] ? <XCircle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
                <span>{RESULT[result.status] || result.status}{result.message_id ? ` · ID ${result.message_id}` : ""}{result.error ? ` · ${result.error}` : ""}</span>
              </div>
            )}
            {warn && <p className="rounded-lg border border-orange-300 bg-orange-50 p-2 text-xs text-orange-800" data-testid="send-retry-warning"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{warn}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="invoice-send-cancel">Close</Button>
          {BAD[result?.status] ? <Button onClick={retry} disabled={busy} data-testid="invoice-send-retry" className="bg-slate-900 text-white">{busy ? "Retrying…" : warn ? "Retry anyway" : "Retry"}</Button>
            : !result && <Button onClick={() => send(false)} disabled={busy || !pv?.valid || !live} data-testid="invoice-send-confirm-btn" className="bg-emerald-600 text-white hover:bg-emerald-700">{busy ? "Sending…" : "Send"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
